import { isBroadcastChannelSupported, isInIframe } from 'src/app/utils/in-app-comunication/utils';
import * as bc from 'src/app/utils/in-app-comunication/broadcast-channel';
import * as wm from 'src/app/utils/in-app-comunication/window-messages';
import { AppMessage } from 'src/app/utils/in-app-comunication/types';
import React, { useContext, useEffect, useState } from 'react';

export type MessageHandler = (msg: AppMessage) => void;

class CommunicationService {
  private useBroadcastChannel: boolean = false;

  private constructor(useBroadcastChannel: boolean) {
    this.useBroadcastChannel = useBroadcastChannel;
  }

  static async create(): Promise<CommunicationService> {
    const supported = await isBroadcastChannelSupported();
    const inIframe = isInIframe();
    const useBroadcastChannel = supported && !inIframe;
    return new CommunicationService(useBroadcastChannel);
  }

  sendMessage(message: AppMessage): void {
    if (this.useBroadcastChannel) {
      bc.sendMessage(message);
    } else if (isInIframe()) {
      wm.sendMessage(message);
    } else {
      bc.sendMessage(message);
    }
  }

  onMessage(handler: MessageHandler): () => void {
    if (this.useBroadcastChannel) {
      return bc.onMessage(handler);
    } else if (isInIframe()) {
      return wm.onMessage(handler);
    } else {
      return bc.onMessage(handler);
    }
  }

  closeBroadcastChannel(): void {
    bc.closeBroadcastChannel();
  }
}

const CommunicationContext = React.createContext<CommunicationService | null>(null);

export const CommunicationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [service, setService] = useState<CommunicationService | null>(null);
  useEffect(() => {
    void CommunicationService.create().then(setService);
  }, []);

  if (!service) return null;
  return <CommunicationContext.Provider value={service}>{children}</CommunicationContext.Provider>;
};

export const useCommunicationService = () => {
  const service = useContext(CommunicationContext);
  if (!service) throw new Error('CommunicationService not initialized');
  return service;
};

export const createCommunicationService = CommunicationService.create;
export type { CommunicationService };
export default CommunicationService;
